import {z} from 'zod';

import i18n from '@dms/i18n';

const ALPHABET_PATTERN = /^[A-Z]{1,2}$/;
const NUMBERS_PATTERN = /^\d+$/;
const MIN_NUMBER = 1;
const MAX_NUMBER = 99;

export const vectorTypeSchema = z.enum(['AISLE', 'RACK', 'LEVEL', 'POSITION', 'OTHER']);
export type VectorType = z.infer<typeof vectorTypeSchema>;

export const symbolTypeSchema = z.enum(['ALPHABET', 'NUMBERS']);
export type SymbolType = z.infer<typeof symbolTypeSchema>;

const locationRowSchema = z.object({
  isSelected: z.boolean(),
  beginAt: z.string(),
  number: z
    .number({
      required_error: i18n.t('errors.field_is_required'),
      invalid_type_error: i18n.t('errors.field_is_required'),
    })
    .min(MIN_NUMBER, i18n.t('general.validations.minValue', {min: MIN_NUMBER}))
    .max(MAX_NUMBER, i18n.t('general.validations.maxValue', {max: MAX_NUMBER})),
  vectorType: vectorTypeSchema,
  vectorPosition: z.number(),
  symbolType: symbolTypeSchema,
});

export type LocationRow = z.infer<typeof locationRowSchema>;

export const multipleLocationsFormSchema = z
  .object({
    definition: z.array(locationRowSchema),
    allowMultipleUsage: z.boolean(),
  })
  .superRefine((data, ctx) => {
    data.definition.forEach((row, index) => {
      if (!row.isSelected) {
        return;
      }

      if (row.beginAt === '') {
        ctx.addIssue({
          path: ['definition', index, 'beginAt'],
          code: z.ZodIssueCode.custom,
          message: i18n.t('general.validations.fieldIsRequired'),
        });
        return;
      }

      if (row.symbolType === 'ALPHABET' && !ALPHABET_PATTERN.test(row.beginAt)) {
        ctx.addIssue({
          path: ['definition', index, 'beginAt'],
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.tireWarehouses.validations.onlyTwoLettersAllowed'),
        });
        return;
      }

      if (row.symbolType === 'NUMBERS' && !NUMBERS_PATTERN.test(row.beginAt)) {
        ctx.addIssue({
          path: ['definition', index, 'beginAt'],
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.tireWarehouses.validations.onlyNumbersAllowed'),
        });
      }
    });
  });

export type MultipleLocationsFormType = z.infer<typeof multipleLocationsFormSchema>;
